import { m } from 'motion/react';
import { useId, type ReactNode } from 'react';

import { fieldStyles } from '../styles';
import { cn } from '../../lib/cn';

/**
 * Los controles de los que está hecho el editor de fichas.
 *
 * Todos reciben un valor y un `onChange` en vez de ser no controlados, que es
 * lo que necesita el autoguardado de tres segundos: el borrador vive en un solo
 * trozo de estado que el autoguardado lee, así que nunca hay un campo cuyo
 * texto actual solo conozca el DOM.
 *
 * Cada uno ata su error al campo con `aria-describedby`, que es lo que hace que
 * la regla de `13 §6` —un 422 aterriza en el campo que lo causó— funcione
 * también para un lector de pantalla y no solo a la vista.
 */

function Wrapper({
  id,
  label,
  hint,
  error,
  children,
}: {
  id: string;
  label: string;
  hint?: string | undefined;
  error?: string | undefined;
  children: ReactNode;
}) {
  return (
    <div className="space-y-1.5">
      <label htmlFor={id} className="block text-sm font-semibold text-ink">
        {label}
      </label>
      {children}
      {error ? (
        <p id={`${id}-error`} role="alert" className="animate-rise text-xs font-semibold text-danger">
          {error}
        </p>
      ) : hint ? (
        <p id={`${id}-hint`} className="text-xs leading-relaxed text-ink-soft">
          {hint}
        </p>
      ) : null}
    </div>
  );
}

/** Un solo aspecto para todo lo que se escribe, compartido con `Field`. */
const control = (error?: string) => cn(fieldStyles, error ? 'border-danger focus:border-danger focus:shadow-danger/15' : 'border-line');

export function TextField({
  label,
  value,
  onChange,
  hint,
  error,
  rows,
  placeholder,
  maxLength,
}: {
  label: string;
  value: string;
  onChange: (value: string) => void;
  hint?: string | undefined;
  error?: string | undefined;
  rows?: number;
  placeholder?: string;
  maxLength?: number;
}) {
  const id = useId();
  const described = error ? `${id}-error` : hint ? `${id}-hint` : undefined;

  return (
    <Wrapper id={id} label={label} hint={hint} error={error}>
      {rows ? (
        <textarea
          id={id}
          rows={rows}
          value={value}
          placeholder={placeholder}
          maxLength={maxLength}
          aria-invalid={error ? true : undefined}
          aria-describedby={described}
          onChange={(event) => onChange(event.target.value)}
          className={cn(control(error), 'resize-y')}
        />
      ) : (
        <input
          id={id}
          value={value}
          placeholder={placeholder}
          maxLength={maxLength}
          aria-invalid={error ? true : undefined}
          aria-describedby={described}
          onChange={(event) => onChange(event.target.value)}
          className={control(error)}
        />
      )}
    </Wrapper>
  );
}

export function NumberField({
  label,
  value,
  onChange,
  hint,
  error,
  min,
  max,
}: {
  label: string;
  value: number;
  onChange: (value: number) => void;
  hint?: string | undefined;
  error?: string | undefined;
  min?: number;
  max?: number;
}) {
  const id = useId();

  return (
    <Wrapper id={id} label={label} hint={hint} error={error}>
      <input
        id={id}
        type="number"
        value={value}
        min={min}
        max={max}
        aria-invalid={error ? true : undefined}
        aria-describedby={error ? `${id}-error` : hint ? `${id}-hint` : undefined}
        // `valueAsNumber` is NaN for an empty box, and NaN in the draft becomes
        // `null` in JSON and a schema failure the user cannot see the cause of.
        onChange={(event) => onChange(Number.isNaN(event.target.valueAsNumber) ? 0 : event.target.valueAsNumber)}
        className={control(error)}
      />
    </Wrapper>
  );
}

export function SelectField({
  label,
  value,
  options,
  onChange,
  hint,
  error,
  missingLabel,
}: {
  label: string;
  value: string;
  options: { value: string; label: string }[];
  onChange: (value: string) => void;
  hint?: string | undefined;
  error?: string | undefined;
  /** Cómo se rotula un valor guardado que ya no está entre las opciones. */
  missingLabel?: ((value: string) => string) | undefined;
}) {
  const id = useId();

  return (
    <Wrapper id={id} label={label} hint={hint} error={error}>
      <select
        id={id}
        value={value}
        aria-invalid={error ? true : undefined}
        aria-describedby={error ? `${id}-error` : hint ? `${id}-hint` : undefined}
        onChange={(event) => onChange(event.target.value)}
        className={cn(control(error), 'cursor-pointer appearance-none bg-[length:16px] bg-[right_0.75rem_center] bg-no-repeat pr-9')}
        style={{
          // La flecha del `select` nativo no se puede teñir por CSS y en el
          // tema oscuro quedaba negra sobre negro. Va como dato URI con
          // `currentColor` resuelto a la tinta suave del tema.
          backgroundImage:
            "url(\"data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 24 24' fill='none' stroke='%238f96a8' stroke-width='2.5' stroke-linecap='round'%3E%3Cpath d='m6 9 6 6 6-6'/%3E%3C/svg%3E\")",
        }}
      >
        {/* Un valor guardado que no está entre las opciones se muestra como
            opción propia, no se traga. Si no, el `select` nativo pinta el
            primero de la lista y el formulario dice que el campo vale una cosa
            mientras el servidor tiene otra — que es justo el caso en el que hay
            un error debajo pidiendo cambiarlo. */}
        {value !== '' && !options.some((option) => option.value === value) ? (
          <option value={value}>{missingLabel ? missingLabel(value) : value}</option>
        ) : null}
        {options.map((option) => (
          <option key={option.value} value={option.value}>
            {option.label}
          </option>
        ))}
      </select>
    </Wrapper>
  );
}

export function SwitchField({
  label,
  checked,
  onChange,
  hint,
  disabled = false,
}: {
  label: string;
  checked: boolean;
  onChange: (checked: boolean) => void;
  hint?: string;
  /**
   * Apagado pero visible, para los interruptores que tienen una ventana: se ve
   * cómo está y, en el `hint`, por qué ya no se toca. Esconderlo diría que la
   * opción no existe, que es otra cosa.
   */
  disabled?: boolean;
}) {
  const id = useId();

  /*
    Un interruptor de verdad y no una casilla: el estado se lee de un vistazo en
    una pantalla llena de ellos, que es exactamente el caso de la ficha del
    empleado y de las preferencias de notificación. El `input` sigue ahí,
    invisible pero presente, porque es lo que hace que el control siga siendo
    enfocable, marcable con teclado y anunciado como casilla.
  */
  return (
    <label htmlFor={id} className={cn('flex items-start gap-3', disabled ? 'cursor-default opacity-60' : 'cursor-pointer')}>
      <span className="relative mt-0.5 inline-flex shrink-0">
        <input
          id={id}
          type="checkbox"
          checked={checked}
          disabled={disabled}
          onChange={(event) => onChange(event.target.checked)}
          className={cn('peer absolute size-full opacity-0', disabled ? 'cursor-default' : 'cursor-pointer')}
        />
        <span
          aria-hidden="true"
          className={cn(
            'flex h-5.5 w-9.5 items-center rounded-full p-0.5 transition-colors duration-fast',
            'peer-focus-visible:outline peer-focus-visible:outline-2 peer-focus-visible:outline-offset-2 peer-focus-visible:outline-primary',
            checked ? 'bg-primary' : 'bg-line-strong/45',
          )}
        >
          <m.span layout transition={{ type: 'spring', stiffness: 620, damping: 34 }} className={cn('size-4.5 rounded-full bg-white shadow-xs', checked && 'ml-auto')} />
        </span>
      </span>

      <span className="text-sm text-ink">
        {label}
        {hint ? <span className="mt-0.5 block text-xs leading-relaxed text-ink-soft">{hint}</span> : null}
      </span>
    </label>
  );
}

/**
 * Una lista separada por comas, para los campos con forma de etiqueta —pilas
 * tecnológicas, dominios, competencias—.
 *
 * Texto y no un widget de etiquetas a propósito: son cortas, el gerente suele
 * escribir varias de una vez, y un widget que se traga una coma es la clase de
 * cosa que hace que la gente reescriba la línea entera.
 */
export function TagsField({
  label,
  values,
  onChange,
  hint,
  error,
}: {
  label: string;
  values: string[];
  onChange: (values: string[]) => void;
  hint?: string | undefined;
  error?: string | undefined;
}) {
  return (
    <TextField
      label={label}
      value={values.join(', ')}
      hint={hint}
      error={error}
      onChange={(value) =>
        onChange(
          value
            .split(',')
            .map((item) => item.trim())
            .filter((item) => item !== ''),
        )
      }
    />
  );
}
